import { ScreenContext, ScreenKind } from "../types";

/** The ids the app routes on are UUIDs; anything else is not sent as a screen. */
const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Path segments that sit where an id would, but name a page instead. */
const NOT_AN_ID = new Set([
  "approval-rules",
  "settings",
  "coordinate",
  "token",
  "new",
  "import",
  "upload",
  "bulk",
  "from-request",
  "multiple",
]);

type Side = "buyer" | "seller";

/** A route pattern: `:id` is the record, `*` lets sub-pages count as the same record. */
type Rule = { pattern: string[]; kind: ScreenKind };

const rule = (path: string, kind: ScreenKind): Rule => ({
  pattern: path.split("/").filter(Boolean),
  kind,
});

const BUYER_RULES: Rule[] = [
  rule("/orders/multiple/:id/*", "joint_order"),
  rule("/orders/:id/*", "order"),
  rule("/orders", "orders"),
  rule("/purchase-requests-v2/:id/*", "purchase_request"),
  rule("/purchase-requests/:id/*", "purchase_request"),
  rule("/purchase-requests", "purchase_requests"),
  rule("/suppliers/:id/*", "supplier"),
  rule("/materials/:id/*", "material"),
  rule("/deliveries/:id/*", "delivery"),
  rule("/purchase-orders/:id/*", "purchase_order"),
  rule("/purchase-orders", "purchase_orders"),
  rule("/deliveries", "deliveries"),
  rule("/analytics", "analytics"),
  rule("/users/:id/*", "user"),
  rule("/users", "users"),
  rule("/sites/:id/*", "site"),
  rule("/chat", "chats"),
  rule("/", "home"),
];

const SELLER_RULES: Rule[] = [
  rule("/orders/multiple/:id/*", "joint_order"),
  rule("/orders/:id/*", "order"),
  // The supplier's sent joint offer is routed by one of the RFQ's own orders.
  rule("/offer/multiple/:id/*", "joint_order"),
  rule("/offer/:id/*", "offer"),
  rule("/deliveries/:id/*", "delivery"),
  rule("/biddings", "biddings"),
  rule("/purchase-orders", "purchase_orders"),
  rule("/deliveries", "deliveries"),
  rule("/dashboard", "opportunities"),
  rule("/opportunities/material/:id/*", "opportunity"),
  rule("/material/offers", "material_offers"),
  rule("/users/:id/*", "user"),
  rule("/users", "users"),
  rule("/company", "company"),
  rule("/chat", "chats"),
  rule("/", "home"),
];

const match = (rule: Rule, segments: string[]): ScreenContext | null => {
  let id: string | null = null;
  for (let i = 0; i < rule.pattern.length; i++) {
    const part = rule.pattern[i];
    if (part === "*") return { kind: rule.kind, id };
    const segment = segments[i];
    if (segment === undefined) return null;
    if (part === ":id") {
      if (NOT_AN_ID.has(segment)) return null;
      id = segment;
    } else if (part !== segment) {
      return null;
    }
  }
  return segments.length === rule.pattern.length
    ? { kind: rule.kind, id }
    : null;
};

/**
 * What the user is looking at, read from the route alone: `/orders/:id` is an order, its
 * sub-pages are the same order, a list page has no id. `null` for a screen the assistant has no
 * context for, and for an id that is not one — the backend checks access either way.
 */
export const screenContextFromPath = (
  pathname: string,
  side: Side
): ScreenContext | null => {
  const segments = pathname.split("/").filter(Boolean);
  const rules = side === "seller" ? SELLER_RULES : BUYER_RULES;
  for (const candidate of rules) {
    const context = match(candidate, segments);
    if (!context) continue;
    if (context.id !== null && !ID.test(context.id)) return null;
    return context;
  }
  return null;
};

/** Signed-in pages where the floating button would get in the way. */
const HIDDEN_PREFIXES = [
  "/onboarding",
  "/validate-user",
  "/company-review",
  "/purchase-orders/token",
];

export const isAssistantHidden = (pathname: string): boolean =>
  HIDDEN_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
